import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import {
  ArrowLeft,
  Clapperboard,
  Download,
  FileText,
  FileUp,
  Loader2,
  Lock,
  Save,
  Store,
  Trash2,
} from 'lucide-react'

import {
  type Production,
  type Script,
  type ScriptComment,
  type ScriptEpisodeSummary,
  type ScriptRevision,
  type StudioProductionListing,
} from '@/api/studio-production'
import { AuthSessionExpiredError } from '@/api/authSession'
import { ScriptListingModal } from '@/features/studio/modals/ScriptListingModal'
import { ScriptEpisodeModal } from '@/features/studio/modals/ScriptEpisodeModal'
import { WriterScriptListingModal } from '@/features/studio/modals/WriterScriptListingModal'
import { WriterImportToProductionModal } from '@/features/studio/modals/WriterImportToProductionModal'
import {
  ScriptEditorTree,
  isSeriesOutlineSelection,
  isStoryContentSelection,
  treeSelectionEpisodeNo,
  type EditorTreeSelection,
} from '@/features/studio/components/ScriptEditorTree'
import { ScriptEditorAIPanel } from '@/features/studio/components/ScriptEditorAIPanel'
import { WriterScriptEditorAIPanel } from '@/features/studio/components/WriterScriptEditorAIPanel'
import { ScriptCommentPanel } from '@/features/studio/components/ScriptCommentPanel'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { Button } from '@/shared/components/ui/button'
import { ProtoBadge, ProtoBtn, ProtoInput, ProtoSegmented, ProtoTabs } from '@/features/studio/components/ui-kit'
import '@/features/studio/styles/script-editor.css'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import {
  createScriptEditorApi,
  type ScriptEditorVariant,
} from '@/features/studio/lib/script-editor-api'
import { findSceneRangeInBody } from '@/features/studio/script/element-body-range'
import {
  episodeInSeasonIndex,
  formatEpisodeLabel,
  planAddSeason,
  resolveEpisodesPerSeason,
  seasonIndex,
  usesSeasonSplit,
} from '@/features/studio/script/season'
import { deriveScenesFromBody } from '@/features/studio/script/derive-scenes'
import { hasWriterLayerContent } from '@/features/studio/script/layer-content'
import { scrollEditorToTreeSelection } from '@/features/studio/script/scroll-to-target'
import {
  flashTextarea,
  readTextareaSelection,
  resolveAnchorRange,
  sceneNoAtOffset,
  scrollTextareaToRange,
  type ScriptCommentSelection,
} from '@/features/studio/script/script-comment-anchor'
import { normalizeConvention } from '@/features/studio/script/script-format-standard'
import { useScriptFormatPrefs, type ScriptConvention } from '@/features/studio/script/useScriptFormatPrefs'

import { useScriptAutoSave } from '@/features/studio/hooks/useScriptAutoSave'
import { useScriptAutoParse } from '@/features/studio/hooks/useScriptAutoParse'
import { useScriptScrollSpy } from '@/features/studio/hooks/useScriptScrollSpy'
import { useScriptDocumentComments } from '@/features/studio/hooks/useScriptDocumentComments'
import { useLazyScriptEpisodeObserver } from '@/features/studio/hooks/useLazyScriptEpisodeObserver'

const SCRIPT_CONVENTIONS = ['cn', 'hollywood'] as const satisfies readonly ScriptConvention[]

function isAuthExpired(err: unknown): err is AuthSessionExpiredError {
  return err instanceof AuthSessionExpiredError
}

export function ScriptEditorPage({ variant = 'production' }: { variant?: ScriptEditorVariant }) {
  const { productionId = '', scriptId: scriptIdParam = '', episodeNo: episodeNoParam = '1' } = useParams()
  const navigate = useNavigate()
  const parsedScriptId = Number.parseInt(scriptIdParam, 10)
  const scriptIdValid = Number.isFinite(parsedScriptId) && parsedScriptId >= 1
  const scriptId = scriptIdValid ? parsedScriptId : 0
  const episodeNo = Math.max(1, Number.parseInt(episodeNoParam, 10) || 1)
  const api = useMemo(
    () => createScriptEditorApi(variant, productionId, scriptId),
    [variant, productionId, scriptId],
  )
  const isPersonal = api.isPersonal
  const { t } = useTranslation()
  const { prefs, setPrefs } = useScriptFormatPrefs()
  const episodeTextareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({})

  const [production, setProduction] = useState<Production | null>(null)
  const [seasonBodies, setSeasonBodies] = useState<Record<number, string>>({})
  const [seasonSavedBodies, setSeasonSavedBodies] = useState<Record<number, string>>({})
  const [seasonScripts, setSeasonScripts] = useState<Record<number, Script | null>>({})
  const [seasonTitles, setSeasonTitles] = useState<Record<number, string>>({})
  const [seasonSavedTitles, setSeasonSavedTitles] = useState<Record<number, string>>({})
  const titleSaveTimersRef = useRef<Record<number, number>>({})
  const outlineSaveTimersRef = useRef<Record<number, number>>({})
  const [seasonOutlines, setSeasonOutlines] = useState<Record<number, string>>({})
  const [seasonSavedOutlines, setSeasonSavedOutlines] = useState<Record<number, string>>({})
  const savedSnapshotRef = useRef({ body: '', outline: '' })
  const [_focusEpisodeNo, setFocusEpisodeNo] = useState(episodeNo)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rightTab, setRightTab] = useState<'ai' | 'notes' | 'versions'>('ai')
  const [revisions, setRevisions] = useState<ScriptRevision[]>([])
  const [, setActiveSceneNo] = useState<number | null>(null)
  const [commentDraft, setCommentDraft] = useState('')
  const [commentSelection, setCommentSelection] = useState<ScriptCommentSelection | null>(null)
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null)
  const {
    comments,
    total: commentTotal,
    loading: commentsLoading,
    loadingMore: commentsLoadingMore,
    hasMore: commentsHasMore,
    reload: reloadComments,
    loadMore: loadMoreComments,
  } = useScriptDocumentComments({
    productionId,
    scriptId,
    writer: isPersonal,
    enabled: !loading,
  })
  const [publishing, setPublishing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [breakingDown, setBreakingDown] = useState(false)
  const [breakdownMsg, setBreakdownMsg] = useState<string | null>(null)
  const [listingOpen, setListingOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [scriptListing, setScriptListing] = useState<StudioProductionListing | null>(null)
  const [episodeSwitching, setEpisodeSwitching] = useState(false)
  const [treeSelection, setTreeSelection] = useState<EditorTreeSelection>({
    kind: 'episode',
    episodeNo: Math.max(1, Number.parseInt(episodeNoParam, 10) || 1),
  })
  const [seriesOutline, setSeriesOutline] = useState('')
  const [seriesSaved, setSeriesSaved] = useState('')
  const [seriesSaving, setSeriesSaving] = useState(false)
  const [storyContent, setStoryContent] = useState('')
  const [storySaved, setStorySaved] = useState('')
  const [storySaving, setStorySaving] = useState(false)
  const [scriptDocTitle, setScriptDocTitle] = useState('')
  const [scriptDocPlanning, setScriptDocPlanning] = useState({
    episode_target: 1,
    episodes_per_season: 0,
    default_locale: 'zh-CN',
  })
  const [createEpisodeOpen, setCreateEpisodeOpen] = useState(false)
  const [deleteEpisodeTarget, setDeleteEpisodeTarget] = useState<number | null>(null)
  const [deleteEpisodeBusy, setDeleteEpisodeBusy] = useState(false)
  const [episodeList, setEpisodeList] = useState<{ episode_target: number; episodes_per_season: number; items: ScriptEpisodeSummary[] } | null>(null)
  const paperScrollRef = useRef<HTMLDivElement>(null)
  const scrollSpyPausedRef = useRef(false)
  const urlEpisodeRef = useRef(episodeNo)
  const episodeNoRef = useRef(episodeNo)
  const loadGenRef = useRef(0)
  const loadedSeasonEpsRef = useRef<Set<number>>(new Set())
  const lazyLoadingEpsRef = useRef<Set<number>>(new Set())
  const pendingCommentJumpRef = useRef<ScriptComment | null>(null)
  const [loadingEpisodeNos, setLoadingEpisodeNos] = useState<Set<number>>(() => new Set())

  episodeNoRef.current = episodeNo

  useEffect(() => {
    urlEpisodeRef.current = episodeNo
  }, [episodeNo])

  const loadedDbEpisodeNos = useMemo(
    () =>
      Object.keys(seasonBodies)
        .map((k) => Number.parseInt(k, 10))
        .filter((n) => !Number.isNaN(n)),
    [seasonBodies],
  )

  const episodeTarget = episodeList?.episode_target ?? scriptDocPlanning.episode_target
  const episodesPerSeasonStored = episodeList?.episodes_per_season ?? scriptDocPlanning.episodes_per_season
  const editorEpisodeNos = useMemo(() => {
    const target = episodeTarget
    const max = Math.max(
      target,
      ...(episodeList?.items.map((i) => i.episode_no) ?? []),
      ...loadedDbEpisodeNos,
    )
    const nos: number[] = []
    for (let n = 1; n <= max; n++) {
      if (n <= target || episodeList?.items.some((i) => i.episode_no === n) || loadedDbEpisodeNos.includes(n)) {
        nos.push(n)
      }
    }
    return nos.length ? nos : [1]
  }, [episodeTarget, episodeList, loadedDbEpisodeNos])
  const activeEpisodeNo = treeSelectionEpisodeNo(treeSelection) ?? episodeNo
  const script = seasonScripts[activeEpisodeNo] ?? null
  const body = seasonBodies[activeEpisodeNo] ?? ''
  const outline = seasonOutlines[activeEpisodeNo] ?? ''
  const activeEpIsDraft = (seasonScripts[activeEpisodeNo]?.status ?? 'draft') === 'draft'
  const headerTitle = isPersonal
    ? scriptDocTitle || t('script.unnamedScript', { n: scriptId })
    : scriptDocTitle || production?.title || t('script.title')
  const headerSubtitle = useMemo(() => {
    if (isSeriesOutlineSelection(treeSelection)) {
      return t('script.editorSeriesContext', { target: episodeTarget })
    }
    if (treeSelection.kind === 'story') {
      return t('script.editorStoryContext', { target: episodeTarget })
    }
    if (treeSelection.kind === 'season') {
      return t('script.seasonTitle', { season: treeSelection.seasonNo })
    }
    const epLabel = formatEpisodeLabel(activeEpisodeNo, episodesPerSeasonStored, episodeTarget)
    const epTitle = (seasonTitles[activeEpisodeNo] ?? seasonScripts[activeEpisodeNo]?.title ?? '').trim()
    return epTitle ? `${epLabel} · ${epTitle}` : epLabel
  }, [
    treeSelection,
    activeEpisodeNo,
    episodesPerSeasonStored,
    episodeTarget,
    seasonScripts,
    seasonTitles,
    t,
  ])
  const labelEpisodeTarget = episodeTarget

  /** 全集连续滚动编辑（跨季）；多剧本时仅加载当前剧本槽位内的分集 */
  const scenesByEpisode = useMemo(() => {
    const out: Record<number, { scene_no: number; heading: string }[]> = {}
    for (const epNo of editorEpisodeNos) {
      const epBody = seasonBodies[epNo] ?? ''
      out[epNo] = deriveScenesFromBody(epBody, prefs.convention).map((s) => ({
        scene_no: s.scene_no,
        heading: s.heading,
      }))
    }
    return out
  }, [seasonBodies, editorEpisodeNos, prefs.convention])

  const treeEpisodes = useMemo((): ScriptEpisodeSummary[] => {
    const perSeason = resolveEpisodesPerSeason(episodesPerSeasonStored, labelEpisodeTarget)
    return editorEpisodeNos.map((epNo) => {
      const epScript = seasonScripts[epNo]
      const epBody = seasonBodies[epNo] ?? ''
      const epTitle = seasonTitles[epNo] ?? epScript?.title ?? ''
      return {
        episode_no: epNo,
        title: epTitle,
        season_no: seasonIndex(epNo, perSeason),
        episode_in_season: ((epNo - 1) % perSeason) + 1,
        status: (epScript?.status ?? 'draft') as ScriptEpisodeSummary['status'],
        current_revision: epScript?.current_revision ?? 0,
        scene_count: (scenesByEpisode[epNo] ?? []).length,
        has_body: epBody.trim() !== '',
        has_outline: !!((seasonOutlines[epNo] ?? epScript?.outline)?.trim()),
      }
    })
  }, [
    editorEpisodeNos,
    labelEpisodeTarget,
    episodesPerSeasonStored,
    seasonScripts,
    seasonBodies,
    seasonTitles,
    seasonOutlines,
    scenesByEpisode,
  ])

  function updateSeasonBody(epNo: number, next: string) {
    setSeasonBodies((prev) => ({ ...prev, [epNo]: next }))
    if (epNo === activeEpisodeNo) {
      episodeNoRef.current = epNo
    }
  }

  const scheduleEpisodeTitleSave = useCallback(
    (epNo: number, title: string) => {
      setSeasonTitles((prev) => ({ ...prev, [epNo]: title }))
      const timers = titleSaveTimersRef.current
      window.clearTimeout(timers[epNo])
      timers[epNo] = window.setTimeout(() => {
        void (async () => {
          try {
            const data = await api.saveEpisode(scriptId, epNo, { title })
            setSeasonSavedTitles((prev) => ({ ...prev, [epNo]: title }))
            setSeasonScripts((prev) => ({
              ...prev,
              [epNo]: prev[epNo] ? { ...prev[epNo]!, title: data.title ?? title } : data,
            }))
          } catch (e) {
            if (isAuthExpired(e)) return
            setError(resolveApiErrorMessage(e, 'script.saveFailed'))
          }
        })()
      }, 600)
    },
    [api, scriptId],
  )

  const scheduleEpisodeOutlineSave = useCallback(
    (epNo: number, nextOutline: string) => {
      setSeasonOutlines((prev) => ({ ...prev, [epNo]: nextOutline }))
      const timers = outlineSaveTimersRef.current
      window.clearTimeout(timers[epNo])
      timers[epNo] = window.setTimeout(() => {
        void (async () => {
          try {
            const data = await api.saveEpisode(scriptId, epNo, { outline: nextOutline })
            setSeasonSavedOutlines((prev) => ({ ...prev, [epNo]: nextOutline }))
            setSeasonScripts((prev) => ({
              ...prev,
              [epNo]: prev[epNo] ? { ...prev[epNo]!, outline: data.outline ?? nextOutline } : data,
            }))
          } catch (e) {
            if (isAuthExpired(e)) return
            setError(resolveApiErrorMessage(e, 'script.saveFailed'))
          }
        })()
      }, 600)
    },
    [api, scriptId],
  )

  const isDraft = (script?.status ?? 'draft') === 'draft'
  const canFinalize = isPersonal
    ? isDraft
    : production?.is_owner ||
      production?.role === 'director' ||
      production?.role === 'executive_producer' ||
      production?.role === 'producer'
  const isOwner = isPersonal ? true : production?.is_owner
  const canManageEpisodes = isPersonal
    ? activeEpIsDraft
    : production?.is_owner ||
      production?.role === 'producer' ||
      production?.role === 'executive_producer' ||
      production?.role === 'director'
  const canManageScenes = isPersonal ? activeEpIsDraft : canManageEpisodes
  const canListScript = isPersonal
    ? true
    : isOwner ||
      production?.role === 'writer' ||
      production?.role === 'art_director' ||
      production?.role === 'director' ||
      production?.role === 'producer' ||
      production?.role === 'executive_producer'

  const loadDocumentEpisodes = useCallback(async () => {
    try {
      const res = await api.listEpisodes(scriptId)
      setEpisodeList(res)
    } catch {
      setEpisodeList(null)
    }
  }, [api, scriptId])

  const reloadEpisodes = useCallback(async () => {
    await loadDocumentEpisodes()
  }, [loadDocumentEpisodes])

  const loadSidePanels = useCallback(async (ep: number) => {
    try {
      const revRes = await api.listRevisions(scriptId, ep)
      setRevisions(revRes.items)
    } catch {
      /* optional */
    }
  }, [api, scriptId])

  const refreshScriptListing = useCallback(
    async (ep: number, lockedRev?: number | null) => {
      if (isPersonal) return
      try {
        const items = await api.listProductionListings()
        const hit =
          lockedRev != null
            ? items.find(
                (l) =>
                  l.listing_type === 'studio_script' &&
                  l.script_episode_no === ep &&
                  l.script_revision === lockedRev,
              ) ?? null
            : null
        setScriptListing(hit)
      } catch {
        setScriptListing(null)
      }
    },
    [api, isPersonal],
  )

  const applyEpisodeFromScript = useCallback(
    (ep: number, data: Script, opts?: { focus?: boolean }) => {
      loadedSeasonEpsRef.current.add(ep)
      const normalizedBody = (data.body ?? '').replace(/\r\n/g, '\n').trim()
      setSeasonScripts((prev) => ({ ...prev, [ep]: data }))
      setSeasonBodies((prev) => ({ ...prev, [ep]: normalizedBody }))
      setSeasonSavedBodies((prev) => ({ ...prev, [ep]: data.body ?? '' }))
      const epTitle = data.title ?? ''
      setSeasonTitles((prev) => ({ ...prev, [ep]: epTitle }))
      setSeasonSavedTitles((prev) => ({ ...prev, [ep]: epTitle }))
      const epOutline = data.outline ?? ''
      setSeasonOutlines((prev) => ({ ...prev, [ep]: epOutline }))
      setSeasonSavedOutlines((prev) => ({ ...prev, [ep]: epOutline }))
      api.patchEpisodeCache(scriptId, ep, data)
      if (data.script_convention) {
        setPrefs({ convention: normalizeConvention(data.script_convention) })
      }
      const shouldFocus = opts?.focus ?? (ep === episodeNo || ep === activeEpisodeNo)
      if (shouldFocus) {
        savedSnapshotRef.current = { body: normalizedBody, outline: epOutline }
        setActiveSceneNo(data.scenes?.[0]?.scene_no ?? null)
      }
    },
    [api, scriptId, episodeNo, activeEpisodeNo, setPrefs],
  )

  const loadEpisodeLazy = useCallback(
    async (ep: number) => {
      if (!scriptIdValid) return
      if (loadedSeasonEpsRef.current.has(ep) || lazyLoadingEpsRef.current.has(ep)) return
      lazyLoadingEpsRef.current.add(ep)
      setLoadingEpisodeNos((prev) => new Set(prev).add(ep))
      try {
        const data = await api.fetchEpisode(scriptId, ep)
        applyEpisodeFromScript(ep, data, { focus: false })
      } catch (e) {
        if (isAuthExpired(e)) return
        /* optional lazy load */
      } finally {
        lazyLoadingEpsRef.current.delete(ep)
        setLoadingEpisodeNos((prev) => {
          const next = new Set(prev)
          next.delete(ep)
          return next
        })
      }
    },
    [api, scriptId, scriptIdValid, applyEpisodeFromScript],
  )

  const loadEpisode = useCallback(
    async (ep: number) => {
      if (!scriptIdValid) {
        setLoading(false)
        return
      }
      const gen = ++loadGenRef.current
      setEpisodeSwitching(true)
      setLoading(true)
      setError(null)
      try {
        const data = await api.fetchEpisode(scriptId, ep)
        if (loadGenRef.current !== gen) return
        applyEpisodeFromScript(ep, data, { focus: true })
        void loadSidePanels(ep)
        if (!isPersonal) {
          void refreshScriptListing(ep, data.locked_revision)
        }
      } catch (e) {
        if (loadGenRef.current !== gen) return
        if (isAuthExpired(e)) return
        setError(resolveApiErrorMessage(e, isPersonal ? 'writer.editor.loadFailed' : 'script.loadFailed'))
      } finally {
        if (loadGenRef.current === gen) {
          setLoading(false)
          setEpisodeSwitching(false)
        }
      }
    },
    [api, scriptId, scriptIdValid, loadSidePanels, refreshScriptListing, applyEpisodeFromScript, isPersonal],
  )

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!scriptIdValid) return
      try {
        if (isPersonal) {
          const [doc, epList] = await Promise.all([
            api.getScriptDocument(scriptId),
            api.listEpisodes(scriptId),
          ])
          if (cancelled) return
          setEpisodeList(epList)
          setScriptDocTitle(doc.title?.trim() ?? '')
          setScriptDocPlanning({
            episode_target: Math.max(1, doc.episode_target ?? epList.episode_target ?? 1),
            episodes_per_season: Math.max(0, doc.episodes_per_season ?? epList.episodes_per_season ?? 0),
            default_locale: doc.default_locale?.trim() || 'zh-CN',
          })
          const outlineText = doc.series_outline ?? ''
          setSeriesOutline(outlineText)
          setSeriesSaved(outlineText)
          const storyText = doc.story_content ?? ''
          setStoryContent(storyText)
          setStorySaved(storyText)
          return
        }
        const [prod, epList, doc] = await Promise.all([
          api.getProduction(),
          api.listEpisodes(scriptId),
          api.getScriptDocument(scriptId),
        ])
        if (cancelled) return
        if (prod) {
          setProduction(prod)
          if (prod.script_convention) {
            setPrefs({ convention: normalizeConvention(prod.script_convention) })
          }
        }
        setEpisodeList(epList)
        setScriptDocTitle(doc.title?.trim() ?? '')
        setScriptDocPlanning({
          episode_target: Math.max(1, doc.episode_target ?? 1),
          episodes_per_season: Math.max(0, doc.episodes_per_season ?? 0),
          default_locale: doc.default_locale?.trim() || 'zh-CN',
        })
        const outlineText = doc.series_outline ?? ''
        setSeriesOutline(outlineText)
        setSeriesSaved(outlineText)
        const storyText = doc.story_content ?? ''
        setStoryContent(storyText)
        setStorySaved(storyText)
      } catch (e) {
        if (isAuthExpired(e)) return
        /* optional bootstrap */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [api, isPersonal, scriptId, scriptIdValid, setPrefs])

  useEffect(() => {
    void loadEpisode(episodeNo)
  }, [episodeNo, loadEpisode])

  useEffect(() => {
    if (loading) return
    void loadSidePanels(activeEpisodeNo)
    if (!isPersonal) {
      void refreshScriptListing(activeEpisodeNo, seasonScripts[activeEpisodeNo]?.locked_revision)
    }
    const epScript = seasonScripts[activeEpisodeNo]
    if (epScript) {
      savedSnapshotRef.current = {
        body: seasonBodies[activeEpisodeNo] ?? epScript.body ?? '',
        outline: seasonOutlines[activeEpisodeNo] ?? epScript.outline ?? '',
      }
    }
  }, [
    activeEpisodeNo,
    loading,
    loadSidePanels,
    refreshScriptListing,
    seasonScripts,
    seasonBodies,
    seasonOutlines,
    isPersonal,
  ])

  useLazyScriptEpisodeObserver(
    paperScrollRef,
    !isSeriesOutlineSelection(treeSelection) && !isStoryContentSelection(treeSelection) && !loading,
    editorEpisodeNos,
    loadEpisodeLazy,
  )

  useEffect(() => {
    const anchor = episodeNo
    setFocusEpisodeNo(anchor)
    setTreeSelection({ kind: 'episode', episodeNo: anchor })
    scrollSpyPausedRef.current = true
    requestAnimationFrame(() => {
      scrollEditorToTreeSelection(paperScrollRef.current, { kind: 'episode', episodeNo: anchor })
      window.setTimeout(() => {
        scrollSpyPausedRef.current = false
      }, 350)
    })
  }, [episodeNo])

  async function handleSaveSeriesOutline() {
    setSeriesSaving(true)
    setError(null)
    try {
      await api.patchScriptDocument(scriptId, { series_outline: seriesOutline })
      setSeriesSaved(seriesOutline)
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.saveFailed'))
    } finally {
      setSeriesSaving(false)
    }
  }

  async function handleSaveStoryContent() {
    setStorySaving(true)
    setError(null)
    try {
      await api.patchScriptDocument(scriptId, { story_content: storyContent })
      setStorySaved(storyContent)
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.saveFailed'))
    } finally {
      setStorySaving(false)
    }
  }

  function handleAddScene(epNo: number) {
    setFocusEpisodeNo(epNo)
    const epBody = seasonBodies[epNo] ?? ''
    const scenes = deriveScenesFromBody(epBody, prefs.convention)
    const nextNo = scenes.length ? Math.max(...scenes.map((s) => s.scene_no)) + 1 : 1
    const template =
      prefs.convention === 'hollywood' ? 'INT. LOCATION - DAY' : `内景 场景${nextNo} 日`
    const next = epBody.trim() ? `${epBody.trimEnd()}\n\n${template}\n\n` : `${template}\n\n`
    updateSeasonBody(epNo, next)
    setTreeSelection({ kind: 'scene', episodeNo: epNo, sceneNo: nextNo })
    setActiveSceneNo(nextNo)
  }

  async function handleAddInternalEpisode() {
    setError(null)
    const next = Math.max(...editorEpisodeNos, episodeTarget) + 1
    try {
      await api.patchScriptDocument(scriptId, { episode_target: next })
      setScriptDocPlanning((prev) => ({ ...prev, episode_target: next }))
      const data = await api.fetchEpisode(scriptId, next)
      applyEpisodeFromScript(next, data, { focus: true })
      await reloadEpisodes()
      void navigateEpisode(next)
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.addEpisodeFailed'))
    }
  }

  function handleAddEpisodeFromTree() {
    void handleAddInternalEpisode()
  }

  async function handleAddSeasonFromTree() {
    setError(null)
    const count = Math.max(...editorEpisodeNos, episodeTarget, 1)
    const plan = planAddSeason(episodesPerSeasonStored, count)
    try {
      await api.patchScriptDocument(scriptId, {
        episode_target: plan.episode_target,
        episodes_per_season: plan.episodes_per_season,
      })
      setScriptDocPlanning((prev) => ({
        ...prev,
        episode_target: plan.episode_target,
        episodes_per_season: plan.episodes_per_season,
      }))
      const firstNew = plan.episode_target
      const data = await api.fetchEpisode(scriptId, firstNew)
      applyEpisodeFromScript(firstNew, data, { focus: false })
      await reloadEpisodes()
      setTreeSelection({
        kind: 'season',
        seasonNo: seasonIndex(firstNew, plan.episodes_per_season),
      })
      if (isPersonal) {
        void navigateEpisode(firstNew)
      }
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.addSeasonFailed'))
    }
  }

  function handleDeleteScene(epNo: number, sceneNo: number) {
    const epBody = seasonBodies[epNo] ?? ''
    const derived = deriveScenesFromBody(epBody, prefs.convention)
    const range = findSceneRangeInBody(
      epBody,
      derived.map((s) => ({ scene_no: s.scene_no, heading: s.heading, body: s.body })),
      sceneNo,
      prefs.convention,
    )
    if (!range) return
    updateSeasonBody(epNo, epBody.slice(0, range.start) + epBody.slice(range.end))
    setTreeSelection({ kind: 'episode', episodeNo: epNo })
    setActiveSceneNo(null)
  }

  async function handleDeleteEpisodeFromTree(targetNo: number) {
    setDeleteEpisodeTarget(targetNo)
  }

  async function confirmDeleteEpisode() {
    if (deleteEpisodeTarget == null) return
    setDeleteEpisodeBusy(true)
    setError(null)
    const deleted = deleteEpisodeTarget
    try {
      await api.deleteEpisode(scriptId, deleted)
      setDeleteEpisodeTarget(null)
      setSeasonBodies((prev) => {
        const next = { ...prev }
        delete next[deleted]
        return next
      })
      setSeasonSavedBodies((prev) => {
        const next = { ...prev }
        delete next[deleted]
        return next
      })
      setSeasonScripts((prev) => {
        const next = { ...prev }
        delete next[deleted]
        return next
      })
      loadedSeasonEpsRef.current.delete(deleted)
      await reloadEpisodes()
      if (deleted === episodeNo || deleted === activeEpisodeNo) {
        const fallback = Math.max(1, deleted > 1 ? deleted - 1 : 1)
        navigate(api.episodeEditorPath(scriptId, fallback))
      }
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.deleteEpisodeFailed'))
    } finally {
      setDeleteEpisodeBusy(false)
    }
  }

  function handleTreeSelect(sel: EditorTreeSelection) {
    scrollSpyPausedRef.current = true
    window.setTimeout(() => {
      scrollSpyPausedRef.current = false
    }, 800)
    setTreeSelection(sel)
    if (sel.kind === 'scene') {
      setFocusEpisodeNo(sel.episodeNo)
      setActiveSceneNo(sel.sceneNo)
      void loadEpisodeLazy(sel.episodeNo)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollEditorToTreeSelection(paperScrollRef.current, sel))
      })
    } else if (sel.kind === 'episode') {
      setFocusEpisodeNo(sel.episodeNo)
      setActiveSceneNo(null)
      void loadEpisodeLazy(sel.episodeNo)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollEditorToTreeSelection(paperScrollRef.current, sel))
      })
    } else if (sel.kind === 'season') {
      const perSeason = resolveEpisodesPerSeason(episodesPerSeasonStored, episodeTarget)
      const first = editorEpisodeNos.find((n) => seasonIndex(n, perSeason) === sel.seasonNo) ?? editorEpisodeNos[0]
      if (first != null) {
        setFocusEpisodeNo(first)
        void loadEpisodeLazy(first)
      }
      setActiveSceneNo(null)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollEditorToTreeSelection(paperScrollRef.current, sel))
      })
    } else if (sel.kind === 'story') {
      setActiveSceneNo(null)
    }
  }

  const syncFromScroll = useCallback(
    (target: { episodeNo: number; sceneNo: number | null }) => {
      if (scrollSpyPausedRef.current) return
      setFocusEpisodeNo((prev) => (prev === target.episodeNo ? prev : target.episodeNo))
      if (target.sceneNo) {
        setTreeSelection((prev) => {
          if (
            prev.kind === 'scene' &&
            prev.episodeNo === target.episodeNo &&
            prev.sceneNo === target.sceneNo
          ) {
            return prev
          }
          return { kind: 'scene', episodeNo: target.episodeNo, sceneNo: target.sceneNo! }
        })
        setActiveSceneNo((prev) => (prev === target.sceneNo ? prev : target.sceneNo))
      } else {
        setTreeSelection((prev) => {
          if (prev.kind === 'episode' && prev.episodeNo === target.episodeNo) return prev
          return { kind: 'episode', episodeNo: target.episodeNo }
        })
        setActiveSceneNo((prev) => (prev === null ? prev : null))
      }
      if (target.episodeNo !== urlEpisodeRef.current) {
        const urlEp = target.episodeNo
        urlEpisodeRef.current = urlEp
        const path = api.episodeEditorPath(scriptId, urlEp)
        if (window.location.pathname !== path) {
          window.history.replaceState(null, '', path)
        }
      }
    },
    [api, scriptId],
  )

  const scrollMarkerVersion = useMemo(
    () =>
      `${editorEpisodeNos.join(',')}:${editorEpisodeNos
        .map((n) => (seasonBodies[n] ?? '').length)
        .join(',')}:${loading ? 0 : 1}`,
    [editorEpisodeNos, seasonBodies, loading],
  )

  useScriptScrollSpy({
    rootRef: paperScrollRef,
    enabled: !isSeriesOutlineSelection(treeSelection) && !isStoryContentSelection(treeSelection) && !loading,
    markerVersion: scrollMarkerVersion,
    pausedRef: scrollSpyPausedRef,
    onTarget: syncFromScroll,
  })

  async function persistScriptBody() {
    window.clearTimeout(outlineSaveTimersRef.current[activeEpisodeNo])
    const payload: { body?: string; outline?: string; title?: string } = { body }
    const activeOutline = seasonOutlines[activeEpisodeNo] ?? ''
    if (activeOutline !== (seasonSavedOutlines[activeEpisodeNo] ?? '')) {
      payload.outline = activeOutline
    }
    const activeTitle = seasonTitles[activeEpisodeNo] ?? ''
    if (activeTitle !== (seasonSavedTitles[activeEpisodeNo] ?? '')) {
      payload.title = activeTitle
    }
    const data = await api.saveEpisode(scriptId, activeEpisodeNo, payload)
    setSeasonSavedBodies((prev) => ({ ...prev, [activeEpisodeNo]: body }))
    savedSnapshotRef.current.body = body
    if (payload.outline !== undefined) {
      setSeasonSavedOutlines((prev) => ({ ...prev, [activeEpisodeNo]: activeOutline }))
      savedSnapshotRef.current.outline = activeOutline
    }
    if (payload.title !== undefined) {
      setSeasonSavedTitles((prev) => ({ ...prev, [activeEpisodeNo]: activeTitle }))
    }
    api.patchEpisodeCache(scriptId, activeEpisodeNo, data)
    return data
  }

  const isDirty =
    (seasonBodies[activeEpisodeNo] ?? '') !== (seasonSavedBodies[activeEpisodeNo] ?? '') ||
    (seasonOutlines[activeEpisodeNo] ?? '') !== (seasonSavedOutlines[activeEpisodeNo] ?? '') ||
    (seasonTitles[activeEpisodeNo] ?? '') !== (seasonSavedTitles[activeEpisodeNo] ?? '')

  const { scheduleParse } = useScriptAutoParse({
    enabled: !loading && isDraft,
    writer: isPersonal,
    productionId: isPersonal ? undefined : productionId,
    scriptId,
    onParsed: (epNo, data) => {
      setSeasonScripts((prev) => ({ ...prev, [epNo]: data }))
    },
  })

  const { status: saveStatus, flushSave } = useScriptAutoSave({
    enabled:
      !loading &&
      !!script &&
      (!isPersonal ||
        (!isSeriesOutlineSelection(treeSelection) && !isStoryContentSelection(treeSelection))),
    dirty: isDirty,
    onSave: async () => {
      const ep = activeEpisodeNo
      episodeNoRef.current = ep
      try {
        const data = await persistScriptBody()
        setSeasonScripts((prev) => ({
          ...prev,
          [ep]: data,
        }))
        setSeasonBodies((prev) => ({ ...prev, [ep]: data.body ?? '' }))
        setSeasonSavedBodies((prev) => ({ ...prev, [ep]: data.body ?? '' }))
        scheduleParse(ep)
      } catch (e) {
        if (isAuthExpired(e)) return
        setError(resolveApiErrorMessage(e, 'script.saveFailed'))
      }
    },
  })

  const hubPath = api.hubPath
  const editorBase = api.editorBase(scriptId)

  async function handleNavigateAway(targetPath: string) {
    if (isDirty) {
      const ok = await flushSave()
      if (!ok) return
    }
    navigate(targetPath)
  }

  async function navigateEpisode(target: number) {
    if (target === activeEpisodeNo && target === episodeNo) return
    scrollSpyPausedRef.current = true
    setTreeSelection({ kind: 'episode', episodeNo: target })
    setFocusEpisodeNo(target)
    scrollEditorToTreeSelection(paperScrollRef.current, { kind: 'episode', episodeNo: target })
    const urlEp = target
    window.history.replaceState(null, '', `${editorBase}/${urlEp}`)
    window.setTimeout(() => {
      scrollSpyPausedRef.current = false
    }, 800)
  }

  async function handleBackToWorkspace() {
    await handleNavigateAway(hubPath)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const data = await persistScriptBody()
      setSeasonScripts((prev) => ({ ...prev, [activeEpisodeNo]: data }))
      setSeasonBodies((prev) => ({ ...prev, [activeEpisodeNo]: data.body }))
      setSeasonSavedBodies((prev) => ({ ...prev, [activeEpisodeNo]: data.body }))
      const savedOutline = data.outline ?? outline
      setSeasonOutlines((prev) => ({ ...prev, [activeEpisodeNo]: savedOutline }))
      setSeasonSavedOutlines((prev) => ({ ...prev, [activeEpisodeNo]: savedOutline }))
      savedSnapshotRef.current.outline = savedOutline
      scheduleParse(activeEpisodeNo)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'script.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  async function handlePublishRevision() {
    setPublishing(true)
    setError(null)
    try {
      if (body !== script?.body) {
        await persistScriptBody()
      }
      const data = await api.publishRevision(scriptId, activeEpisodeNo)
      setSeasonScripts((prev) => ({ ...prev, [activeEpisodeNo]: data }))
      await loadSidePanels(activeEpisodeNo)
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.publishFailed'))
    } finally {
      setPublishing(false)
    }
  }

  async function handleLockRevision(revNo: number) {
    setError(null)
    try {
      const data = await api.lockRevision(scriptId, activeEpisodeNo, revNo)
      setSeasonScripts((prev) => ({ ...prev, [activeEpisodeNo]: data }))
      await refreshScriptListing(activeEpisodeNo, data.locked_revision)
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.lockFailed'))
    }
  }

  async function handleAddComment() {
    const text = commentDraft.trim()
    if (!text) return
    const targetEpisodeNo = commentSelection?.episodeNo ?? activeEpisodeNo
    const sceneNo =
      commentSelection?.sceneNo ??
      (commentSelection
        ? sceneNoAtOffset(seasonBodies[targetEpisodeNo] ?? '', commentSelection.start, prefs.convention)
        : undefined)
    try {
      await api.createComment(scriptId, targetEpisodeNo, text, {
        scene_no: sceneNo,
        anchor_start: commentSelection?.start,
        anchor_end: commentSelection?.end,
        anchor_quote: commentSelection?.quote ?? undefined,
      })
      setCommentDraft('')
      setCommentSelection(null)
      await reloadComments()
      if (targetEpisodeNo !== activeEpisodeNo) {
        setRightTab('notes')
      }
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.commentFailed'))
    }
  }

  function syncCommentSelection(epNo: number) {
    const ta = episodeTextareaRefs.current[epNo]
    if (!ta) {
      setCommentSelection(null)
      return
    }
    const anchor = readTextareaSelection(ta)
    if (!anchor?.quote) {
      setCommentSelection(null)
      return
    }
    const epBody = seasonBodies[epNo] ?? ta.value
    setCommentSelection({
      episodeNo: epNo,
      ...anchor,
      sceneNo: sceneNoAtOffset(epBody, anchor.start, prefs.convention),
    })
    setRightTab('notes')
  }

  useEffect(() => {
    const pending = pendingCommentJumpRef.current
    if (!pending) return
    const epNo = pending.episode_no
    if (seasonScripts[epNo] == null) return
    const ta = episodeTextareaRefs.current[epNo]
    if (!ta) return
    pendingCommentJumpRef.current = null
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollCommentIntoView(pending))
    })
  }, [seasonScripts, seasonBodies])

  function scrollCommentIntoView(c: ScriptComment) {
    const ta = episodeTextareaRefs.current[c.episode_no]
    const epBody = seasonBodies[c.episode_no] ?? ta?.value ?? ''
    if (c.anchor_start != null && c.anchor_end != null) {
      const range = resolveAnchorRange(epBody, {
        start: c.anchor_start,
        end: c.anchor_end,
        quote: c.anchor_quote,
      })
      if (ta && range) {
        scrollEditorToTreeSelection(paperScrollRef.current, {
          kind: 'episode',
          episodeNo: c.episode_no,
        })
        scrollTextareaToRange(ta, range.start, range.end, paperScrollRef.current)
        flashTextarea(ta)
        return
      }
    }
    if (c.scene_no) {
      scrollEditorToTreeSelection(paperScrollRef.current, {
        kind: 'scene',
        episodeNo: c.episode_no,
        sceneNo: c.scene_no,
      })
      return
    }
    scrollEditorToTreeSelection(paperScrollRef.current, {
      kind: 'episode',
      episodeNo: c.episode_no,
    })
  }

  function handleJumpToComment(c: ScriptComment) {
    setActiveCommentId(c.public_id)
    setRightTab('notes')
    scrollSpyPausedRef.current = true
    window.setTimeout(() => {
      scrollSpyPausedRef.current = false
    }, 800)
    if (c.scene_no) {
      setTreeSelection({ kind: 'scene', episodeNo: c.episode_no, sceneNo: c.scene_no })
      setActiveSceneNo(c.scene_no)
    } else {
      setTreeSelection({ kind: 'episode', episodeNo: c.episode_no })
      setActiveSceneNo(null)
    }
    setFocusEpisodeNo(c.episode_no)
    const ta = episodeTextareaRefs.current[c.episode_no]
    if (seasonScripts[c.episode_no] == null || !ta) {
      pendingCommentJumpRef.current = c
      void loadEpisodeLazy(c.episode_no)
      scrollEditorToTreeSelection(
        paperScrollRef.current,
        c.scene_no
          ? { kind: 'scene', episodeNo: c.episode_no, sceneNo: c.scene_no }
          : { kind: 'episode', episodeNo: c.episode_no },
      )
      return
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollCommentIntoView(c))
    })
  }

  async function handleExport(format: 'fountain' | 'pdf' | 'fdx') {
    setExporting(true)
    setError(null)
    try {
      await api.downloadExport(scriptId, activeEpisodeNo, format, prefs.convention)
    } catch {
      setError(t('script.exportFailed'))
    } finally {
      setExporting(false)
    }
  }

  async function handleBreakdown() {
    setBreakingDown(true)
    setError(null)
    setBreakdownMsg(null)
    try {
      const result = await api.breakdownEpisode(scriptId, activeEpisodeNo)
      setBreakdownMsg(
        t('script.breakdownDone', {
          created: result.shots_created,
          skipped: result.shots_skipped,
        }),
      )
      const data = await api.fetchEpisode(scriptId, activeEpisodeNo)
      applyEpisodeFromScript(activeEpisodeNo, data, { focus: false })
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'script.breakdownFailed'))
    } finally {
      setBreakingDown(false)
    }
  }

  async function handleFinalize() {
    setFinalizing(true)
    setError(null)
    try {
      if (body !== script?.body) {
        await persistScriptBody()
      }
      const data = await api.finalizeEpisode(scriptId, activeEpisodeNo)
      setSeasonScripts((prev) => ({ ...prev, [activeEpisodeNo]: data }))
      setSeasonBodies((prev) => ({ ...prev, [activeEpisodeNo]: data.body }))
      setSeasonSavedBodies((prev) => ({ ...prev, [activeEpisodeNo]: data.body }))
    } catch (e) {
      if (isAuthExpired(e)) return
      setError(resolveApiErrorMessage(e, 'script.finalizeFailed'))
    } finally {
      setFinalizing(false)
    }
  }

  function handleAiApplied(
    nextScript: Script | null,
    text?: string,
    opts?: { selectLastScene?: boolean },
  ) {
    const nextBody = nextScript?.body ?? text
    if (nextScript) {
      applyEpisodeFromScript(activeEpisodeNo, nextScript, { focus: true })
    } else if (text) {
      updateSeasonBody(activeEpisodeNo, text)
    }
    if (opts?.selectLastScene && nextBody) {
      const scenes = deriveScenesFromBody(nextBody, prefs.convention)
      const last = scenes[scenes.length - 1]
      if (last) {
        setTreeSelection({ kind: 'scene', episodeNo: activeEpisodeNo, sceneNo: last.scene_no })
        setActiveSceneNo(last.scene_no)
      }
    }
  }

  if (!scriptIdValid) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0a0a12] px-6 text-sm text-red-300">
        {t('script.invalidScript', { defaultValue: '无效的剧本' })}
      </div>
    )
  }

  if (loading && !seasonScripts[episodeNo]) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0a0a12]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'script-workspace script-editor-fullscreen script-editor-shell fixed inset-0 z-50 flex flex-col overflow-hidden bg-[#0a0a12]',
        prefs.convention === 'hollywood' && 'script-workspace--hollywood',
      )}
    >
      {episodeSwitching ? (
        <div className="pointer-events-none absolute inset-0 z-[60] flex items-center justify-center bg-[#0a0a12]/55">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
        </div>
      ) : null}
      <div className="relative z-10 shrink-0 border-b border-white/[0.06] bg-[#0b0b13]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            {isPersonal ? (
              <Link
                to={hubPath}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-200"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('script.backToList')}
              </Link>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleBackToWorkspace()}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('script.backToList')}
              </Button>
            )}
            <span className="hidden h-4 w-px bg-white/10 sm:block" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-zinc-100">{headerTitle}</p>
              <p className="text-[11px] text-zinc-500">{headerSubtitle}</p>
            </div>
            <ProtoBadge tone={isDraft ? 'amber' : 'sky'}>
              {isDraft
                ? t('script.statusDraft')
                : t('script.statusFinalized', { rev: script?.current_revision ?? 1 })}
            </ProtoBadge>
            {saveStatus === 'saving' ? (
              <span className="text-[10px] text-zinc-500">{t('script.autoSaving')}</span>
            ) : saveStatus === 'dirty' ? (
              <span className="text-[10px] text-amber-400/80">{t('script.unsaved')}</span>
            ) : saveStatus === 'error' ? (
              <span className="text-[10px] text-rose-400/80">{t('script.autoSaveFailed')}</span>
            ) : (
              <span className="text-[10px] text-zinc-600">{t('script.saved')}</span>
            )}
            {script?.locked_revision ? (
              <ProtoBadge tone="violet">
                {t('script.lockedRev', { rev: script.locked_revision })}
              </ProtoBadge>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ProtoBtn size="sm" variant="secondary" disabled={saving} onClick={() => void handleSave()}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {isDraft ? t('script.save') : t('script.saveWorkingCopy')}
            </ProtoBtn>
            {isDraft && canFinalize ? (
              <ProtoBtn size="sm" disabled={finalizing} onClick={() => void handleFinalize()}>
                {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {t('script.finalize')}
              </ProtoBtn>
            ) : !isDraft ? (
              <ProtoBtn size="sm" disabled={publishing} onClick={() => void handlePublishRevision()}>
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {t('script.publishRevision')}
              </ProtoBtn>
            ) : null}
            {!isPersonal && script?.locked_revision ? (
              <ProtoBtn size="sm" variant="secondary" disabled={breakingDown} onClick={() => void handleBreakdown()}>
                {breakingDown ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clapperboard className="h-3.5 w-3.5" />}
                {t('script.breakdown')}
              </ProtoBtn>
            ) : null}
            <ProtoBtn size="sm" variant="secondary" disabled={exporting} onClick={() => void handleExport('fountain')}>
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {t('script.exportFountain')}
            </ProtoBtn>
            <ProtoBtn size="sm" variant="secondary" disabled={exporting} onClick={() => void handleExport('pdf')}>
              {t('script.exportPdf')}
            </ProtoBtn>
            <ProtoBtn size="sm" variant="secondary" disabled={exporting} onClick={() => void handleExport('fdx')}>
              {t('script.exportFdx')}
            </ProtoBtn>
            {script?.locked_revision && canListScript ? (
              isPersonal ? (
                <ProtoBtn size="sm" variant="secondary" onClick={() => setListingOpen(true)}>
                  <Store className="h-3.5 w-3.5" />
                  {t('script.listingTitle')}
                </ProtoBtn>
              ) : scriptListing ? (
                <ProtoBadge tone="emerald">{t('script.listingStatus', { status: scriptListing.status })}</ProtoBadge>
              ) : (
                <ProtoBtn size="sm" variant="secondary" onClick={() => setListingOpen(true)}>
                  <Store className="h-3.5 w-3.5" />
                  {t('script.listScript')}
                </ProtoBtn>
              )
            ) : null}
            {isPersonal ? (
              <ProtoBtn size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
                <FileUp className="h-3.5 w-3.5" />
                {t('writer.import.short')}
              </ProtoBtn>
            ) : null}
          </div>
        </div>

        {breakdownMsg ? (
          <p className="border-t border-white/[0.04] px-4 py-1.5 text-xs text-emerald-400/90">{breakdownMsg}</p>
        ) : null}

        <div className="script-format-bar flex flex-wrap items-center gap-1 border-t border-white/[0.04] px-4 py-1.5">
          <ProtoSegmented
            options={SCRIPT_CONVENTIONS.map((conv) => ({
              value: conv,
              label: conv === 'cn' ? t('script.conventionCn') : t('script.conventionHollywood'),
            }))}
            value={prefs.convention}
            onChange={(conv) => setPrefs({ convention: conv as ScriptConvention })}
          />
          <span className="ml-2 text-[11px] text-zinc-500">
            <FileText className="mr-1 inline h-3.5 w-3.5 align-text-bottom text-zinc-600" />
            {prefs.convention === 'hollywood'
              ? t('script.formatSceneHeadingHollywood')
              : t('script.formatSceneHeadingCn')}
            <span className="mx-1 text-zinc-700">·</span>
            Fountain
          </span>
        </div>
      </div>

      {!isDraft ? (
        <div className="border-b border-sky-500/20 bg-sky-500/10 px-4 py-2 text-xs text-sky-200">
          {t('script.finalizedBanner')}
        </div>
      ) : null}

      {error ? <p className="px-4 py-2 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <ScriptEditorTree
          episodes={treeEpisodes}
          episodeTarget={episodeTarget}
          episodesPerSeasonStored={episodesPerSeasonStored}
          currentEpisodeNo={activeEpisodeNo}
          scenesByEpisode={scenesByEpisode}
          selection={treeSelection}
          onSelect={handleTreeSelect}
          onNavigateEpisode={(no) => void navigateEpisode(no)}
          canManage={!!canManageEpisodes}
          canManageScenes={canManageScenes}
          canManageEpisodes={canManageEpisodes}
          onAddEpisode={handleAddEpisodeFromTree}
          onAddSeason={() => void handleAddSeasonFromTree()}
          onDeleteEpisode={(no) => void handleDeleteEpisodeFromTree(no)}
          onAddScene={handleAddScene}
          onDeleteScene={handleDeleteScene}
          onRenameEpisode={scheduleEpisodeTitleSave}
        />

        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <main
          ref={paperScrollRef}
          className={clsx(
            'script-paper-scroll studio-scrollbar-thin flex min-h-0 min-w-0 flex-1 flex-col overscroll-contain border-r border-white/[0.06]',
            isSeriesOutlineSelection(treeSelection) || isStoryContentSelection(treeSelection)
              ? 'script-paper-scroll--doc overflow-hidden'
              : 'overflow-y-auto',
          )}
        >
          {isSeriesOutlineSelection(treeSelection) ? (
            <ScriptDocTextPane
              title={t('script.treeSeries')}
              hint={t('script.seriesOutlineHint')}
              value={seriesOutline}
              onChange={setSeriesOutline}
              saved={seriesSaved}
              saving={seriesSaving}
              onSave={() => void handleSaveSeriesOutline()}
              placeholder={t('script.seriesOutlinePlaceholder')}
              saveLabel={t('script.saveSeriesOutline')}
              unsavedLabel={t('script.unsaved')}
              savedLabel={t('script.saved')}
            />
          ) : isStoryContentSelection(treeSelection) ? (
            <ScriptDocTextPane
              title={t('script.treeStory')}
              hint={t('script.storyContentHint')}
              value={storyContent}
              onChange={setStoryContent}
              saved={storySaved}
              saving={storySaving}
              onSave={() => void handleSaveStoryContent()}
              placeholder={t('script.storyContentPlaceholder')}
              saveLabel={t('script.saveStoryContent')}
              unsavedLabel={t('script.unsaved')}
              savedLabel={t('script.saved')}
            />
          ) : (
            <div className="space-y-10 pb-16">
              {editorEpisodeNos.map((epNo) => {
                const epLabel = formatEpisodeLabel(epNo, episodesPerSeasonStored, episodeTarget)
                const epBody = seasonBodies[epNo] ?? ''
                const epLoaded = seasonScripts[epNo] != null
                const epLoading = loadingEpisodeNos.has(epNo) || (epNo === episodeNo && loading)
                const perSeason = resolveEpisodesPerSeason(episodesPerSeasonStored, labelEpisodeTarget)
                const relativeEp = epNo
                const seasonNo = seasonIndex(relativeEp, perSeason)
                const isSeasonStart = episodeInSeasonIndex(relativeEp, perSeason) === 1
                const showSeasonDivider =
                  usesSeasonSplit(episodesPerSeasonStored, labelEpisodeTarget) &&
                  isSeasonStart &&
                  relativeEp > 1
                const scenes = deriveScenesFromBody(epBody, prefs.convention)
                return (
                  <section key={epNo} data-episode-no={epNo} className="script-episode-section">
                    {showSeasonDivider ? (
                      <div
                        data-scroll-marker={`season:${seasonNo}`}
                        className="mb-2 mt-4 w-full border-t border-indigo-500/25 px-4 pt-6"
                      >
                        <p className="text-center text-[11px] font-semibold tracking-widest text-indigo-300/80">
                          {t('script.seasonDivider', { season: seasonNo, defaultValue: `第 ${seasonNo} 季` })}
                        </p>
                      </div>
                    ) : isSeasonStart ? (
                      <div data-scroll-marker={`season:${seasonNo}`} className="h-px w-full" aria-hidden />
                    ) : null}
                    <div
                      data-scroll-marker={`episode:${epNo}`}
                      className="sticky top-0 z-10 border-b border-white/[0.06] bg-[#0a0a12]/95 px-4 py-2 backdrop-blur-sm"
                    >
                      <div className="flex w-full flex-wrap items-center gap-2">
                        <span className="shrink-0 text-xs font-semibold tracking-wide text-zinc-500">{epLabel}</span>
                        <ProtoInput
                          value={seasonTitles[epNo] ?? ''}
                          onChange={(e) => scheduleEpisodeTitleSave(epNo, e.target.value)}
                          onFocus={() => setFocusEpisodeNo(epNo)}
                          placeholder={t('script.episodeTitlePlaceholder')}
                          maxLength={255}
                          className="!h-8 min-w-0 flex-1 !text-xs"
                        />
                      </div>
                    </div>
                    {scenes.map((scene) => (
                      <div
                        key={`${epNo}-${scene.scene_no}`}
                        data-scroll-marker={`scene:${epNo}:${scene.scene_no}`}
                        className="pointer-events-none h-px w-full opacity-0"
                        aria-hidden
                      />
                    ))}
                    <div className="w-full px-4 pb-8 pt-2">
                      {!epLoaded ? (
                        <div className="flex min-h-[120px] items-center justify-center gap-2 rounded-xl border border-white/[0.06] bg-[#0e0e16]/80 text-xs text-zinc-500">
                          <Loader2 className={clsx('h-4 w-4 text-indigo-400', epLoading && 'animate-spin')} />
                          {t('script.episodeLoading')}
                        </div>
                      ) : (
                      <>
                      <div className="mb-3 rounded-xl border border-white/[0.06] bg-[#0e0e16]/60 p-3">
                        <p className="text-[11px] font-medium text-zinc-400">{t('script.episodeOutlineLabel')}</p>
                        <p className="mt-0.5 text-[10px] leading-relaxed text-zinc-600">{t('script.outlineHint')}</p>
                        <textarea
                          value={seasonOutlines[epNo] ?? ''}
                          onChange={(e) => scheduleEpisodeOutlineSave(epNo, e.target.value)}
                          onFocus={() => setFocusEpisodeNo(epNo)}
                          spellCheck={false}
                          rows={4}
                          className="script-fountain-field mt-2 w-full resize-y text-[12px] leading-relaxed text-zinc-300"
                          placeholder={t('script.outlinePlaceholder')}
                        />
                      </div>
                      <div className="script-excel-sheet">
                        <div className="script-excel-scroll studio-scrollbar-thin">
                          <textarea
                            ref={(el) => {
                              episodeTextareaRefs.current[epNo] = el
                            }}
                            value={epBody}
                            onChange={(e) => updateSeasonBody(epNo, e.target.value)}
                            onFocus={() => setFocusEpisodeNo(epNo)}
                            onSelect={() => syncCommentSelection(epNo)}
                            onMouseUp={() => syncCommentSelection(epNo)}
                            onKeyUp={() => syncCommentSelection(epNo)}
                            spellCheck={false}
                            className="script-source-view script-fountain-field w-full text-[13px] leading-relaxed text-zinc-200"
                            placeholder={
                              prefs.convention === 'hollywood'
                                ? t('script.placeholderHollywood')
                                : t('script.placeholderCn')
                            }
                          />
                        </div>
                      </div>
                      </>
                      )}
                    </div>
                  </section>
                )
              })}
            </div>
          )}
        </main>

        <aside className="flex min-h-0 min-w-0 flex-1 flex-col bg-[#0b0b13]">
          <div className="border-b border-white/[0.04] px-3 pt-3">
            <ProtoTabs
              tabs={[
                { id: 'ai' as const, label: t('script.tabAi') },
                { id: 'notes' as const, label: t('script.tabNotes') },
                { id: 'versions' as const, label: t('script.tabVersions') },
              ]}
              active={rightTab}
              onChange={setRightTab}
            />
          </div>
          <div
            className={`studio-scrollbar-hidden flex min-h-0 flex-1 flex-col overflow-x-hidden p-4 ${
              rightTab === 'ai' ? 'overflow-y-hidden' : 'overflow-y-auto'
            }`}
          >
            {rightTab === 'ai' ? (
              isPersonal ? (
                <WriterScriptEditorAIPanel
                  scriptId={scriptId}
                  episodeNo={activeEpisodeNo}
                  layer={
                    isSeriesOutlineSelection(treeSelection)
                      ? 'series'
                      : isStoryContentSelection(treeSelection)
                        ? 'story'
                        : 'episode'
                  }
                  body={body}
                  sceneNo={treeSelection.kind === 'scene' ? treeSelection.sceneNo : null}
                  convention={prefs.convention}
                  isDraft={activeEpIsDraft}
                  hasSeriesOutline={hasWriterLayerContent(seriesOutline)}
                  hasStoryContent={hasWriterLayerContent(storyContent)}
                  hasEpisodeOutline={hasWriterLayerContent(outline)}
                  onApplied={handleAiApplied}
                  onApplySeriesOutline={async (text) => {
                    const next = text.trim()
                    if (!next) return
                    setSeriesOutline(next)
                    setSeriesSaving(true)
                    setError(null)
                    try {
                      await api.patchScriptDocument(scriptId, { series_outline: next })
                      setSeriesSaved(next)
                    } catch (e) {
                      if (isAuthExpired(e)) return
                      setError(resolveApiErrorMessage(e, 'script.saveFailed'))
                      throw e
                    } finally {
                      setSeriesSaving(false)
                    }
                  }}
                  onApplyStoryContent={async (text) => {
                    const next = text.trim()
                    if (!next) return
                    setStoryContent(next)
                    setStorySaving(true)
                    setError(null)
                    try {
                      await api.patchScriptDocument(scriptId, { story_content: next })
                      setStorySaved(next)
                    } catch (e) {
                      if (isAuthExpired(e)) return
                      setError(resolveApiErrorMessage(e, 'script.saveFailed'))
                      throw e
                    } finally {
                      setStorySaving(false)
                    }
                  }}
                  onApplyEpisodeOutline={async (text) => {
                    const next = text.trim()
                    if (!next) return
                    window.clearTimeout(outlineSaveTimersRef.current[activeEpisodeNo])
                    setSeasonOutlines((prev) => ({ ...prev, [activeEpisodeNo]: next }))
                    setSeasonSavedOutlines((prev) => ({ ...prev, [activeEpisodeNo]: next }))
                    savedSnapshotRef.current.outline = next
                    await api.saveEpisode(scriptId, activeEpisodeNo, { outline: next })
                  }}
                  onError={setError}
                />
              ) : (
                <ScriptEditorAIPanel productionId={productionId} />
              )
            ) : null}

            {rightTab === 'notes' ? (
              <ScriptCommentPanel
                comments={comments}
                commentDraft={commentDraft}
                onCommentDraftChange={setCommentDraft}
                onAddComment={handleAddComment}
                onResolveComment={(c) =>
                  api.resolveComment(scriptId, c.episode_no, c.public_id, true).then(() => reloadComments())
                }
                onJumpToComment={handleJumpToComment}
                selectionPreview={
                  commentSelection?.quote
                    ? { quote: commentSelection.quote, sceneNo: commentSelection.sceneNo }
                    : null
                }
                activeCommentId={activeCommentId}
                formatEpisodeLabel={(epNo) =>
                  formatEpisodeLabel(epNo, episodesPerSeasonStored, episodeTarget)
                }
                total={commentTotal}
                loading={commentsLoading}
                loadingMore={commentsLoadingMore}
                hasMore={commentsHasMore}
                onLoadMore={loadMoreComments}
              />
            ) : null}

            {rightTab === 'versions' ? (
              <ul className="space-y-2">
                {revisions.length === 0 ? (
                  <p className="text-xs text-zinc-600">{t('script.noRevisions')}</p>
                ) : (
                  revisions.map((r) => (
                    <li
                      key={r.revision_no}
                      className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-[#0e0e16] px-3 py-2"
                    >
                      <div>
                        <p className="text-sm text-zinc-200">
                          Rev {r.revision_no} · {r.revision_color}
                        </p>
                        {r.change_note ? (
                          <p className="mt-0.5 text-[10px] text-zinc-600">{r.change_note}</p>
                        ) : null}
                      </div>
                      {isOwner && script?.locked_revision !== r.revision_no ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          className="text-zinc-500 hover:text-indigo-300"
                          title={t('script.lockRevision')}
                          onClick={() => void handleLockRevision(r.revision_no)}
                        >
                          <Lock className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        </aside>
        </div>
      </div>

      {!isPersonal ? (
        <ScriptListingModal
          open={listingOpen}
          onClose={() => setListingOpen(false)}
          productionId={productionId}
          scriptId={scriptId}
          episodeNo={activeEpisodeNo}
          lockedRevision={script?.locked_revision ?? 0}
          defaultTitle={`${headerTitle} · ${formatEpisodeLabel(activeEpisodeNo, episodesPerSeasonStored, episodeTarget)} Rev ${script?.locked_revision ?? 1}`}
        />
      ) : (
        <WriterScriptListingModal
          open={listingOpen}
          onClose={() => setListingOpen(false)}
          scriptId={scriptId}
          episodeNo={activeEpisodeNo}
          lockedRevision={script?.locked_revision ?? 0}
          defaultTitle={`${headerTitle} · ${formatEpisodeLabel(activeEpisodeNo, episodesPerSeasonStored, episodeTarget)} Rev ${script?.locked_revision ?? 1}`}
        />
      )}

      {!isPersonal ? (
        <ScriptEpisodeModal
          open={createEpisodeOpen}
          onClose={() => setCreateEpisodeOpen(false)}
          productionId={productionId}
          onCreated={(newScriptId) => {
            void (async () => {
              setCreateEpisodeOpen(false)
              navigate(api.episodeEditorPath(newScriptId, 1))
            })()
          }}
        />
      ) : null}

      {isPersonal ? (
        <WriterImportToProductionModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          writerScriptId={scriptId}
          episodeNo={activeEpisodeNo}
        />
      ) : null}

      <ConfirmDialog
        open={deleteEpisodeTarget != null}
        title={t('script.deleteEpisodeTitle')}
        message={
          deleteEpisodeTarget != null
            ? t('script.deleteEpisodeConfirm', {
                label: formatEpisodeLabel(
                  deleteEpisodeTarget,
                  episodesPerSeasonStored,
                  episodeTarget,
                ),
              })
            : null
        }
        icon={Trash2}
        destructive
        busy={deleteEpisodeBusy}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDeleteEpisode()}
        onCancel={() => {
          if (deleteEpisodeBusy) return
          setDeleteEpisodeTarget(null)
        }}
      />
    </div>
  )
}

function ScriptDocTextPane({
  title,
  hint,
  value,
  onChange,
  saved,
  saving,
  onSave,
  placeholder,
  saveLabel,
  unsavedLabel,
  savedLabel,
}: {
  title: string
  hint: string
  value: string
  onChange: (next: string) => void
  saved: string
  saving: boolean
  onSave: () => void
  placeholder: string
  saveLabel: string
  unsavedLabel: string
  savedLabel: string
}) {
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="sticky top-0 z-10 shrink-0 border-b border-white/[0.06] bg-[#0a0a12]/95 px-4 py-2 backdrop-blur-sm">
        <h2 className="text-xs font-semibold tracking-wide text-zinc-200">{title}</h2>
        <p className="mt-0.5 text-[10px] leading-relaxed text-zinc-600">{hint}</p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-2">
        <div className="script-excel-sheet script-excel-sheet--fill min-h-0 flex-1">
          <div className="script-excel-scroll studio-scrollbar-thin">
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              spellCheck={false}
              className="script-source-view script-fountain-field w-full text-[13px] leading-relaxed text-zinc-200"
              placeholder={placeholder}
            />
          </div>
        </div>
        <div className="mt-3 flex shrink-0 items-center gap-3">
          <ProtoBtn size="sm" disabled={saving || value === saved} onClick={onSave}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saveLabel}
          </ProtoBtn>
          {value !== saved ? (
            <span className="text-xs text-amber-400/90">{unsavedLabel}</span>
          ) : (
            <span className="text-xs text-zinc-600">{savedLabel}</span>
          )}
        </div>
      </div>
    </div>
  )
}
